import i18n from 'modules/core/lib/i18n'

export const INCIDENCE_STATUSES = {
  in_preparation: 'in-preparation',
  to_do: 'to-do',
  doing: 'doing',
  done: 'done',
  discarded: 'discarded',
} as const

export const INHOUSE_INCIDENCE_STATUSES = {
  to_do: 'to-do',
  doing: 'doing',
  done: 'done',
  discarded: 'discarded',
} as const

export const DATA_VIEW = {
  bookkeeper: 'bookkeeper',
  incidence_manager: 'incidence-manager',
  payroll_in_house: 'payroll-in-house',
} as const

/**
 * This is the superset of all incidence statuses. While `open` is no longer
 * used, `reopened` is automatically applied if a resolved incidence receives an
 * update.
 *
 * @see ElectiveIncidenceStatus
 */
export type IncidenceStatus =
  | 'in-preparation'
  | 'to-do'
  | 'doing'
  | 'done'
  | 'discarded'
  | 'reopened'
  | 'open'
/**
 * These is the subset of incidence statuses that exist for Inhouse Payroll
 */
export type InhouseIncidenceStatus = Exclude<
  IncidenceStatus,
  'in-preparation' | 'reopened' | 'open'
>
/**
 * This is the typical subset of incidence statuses that apply in Professionals, excluding the `reopen` and `open` states.
 */
export type ElectiveIncidenceStatus = Exclude<IncidenceStatus, 'reopened' | 'open'>

export function inhouseStatusCopies(): Record<InhouseIncidenceStatus, string> {
  const statusMap: Record<InhouseIncidenceStatus, string> = {
    'to-do': i18n.t('bookkeepers.incidence.status.to_do'),
    doing: i18n.t('bookkeepers.incidence.status.doing'),
    done: i18n.t('bookkeepers.incidence.status.done'),
    discarded: i18n.t('bookkeepers.incidence.status.discarded'),
  }
  return statusMap
}

export function professionalsStatusCopies(
  isBookkeeperView = false
): Record<ElectiveIncidenceStatus, string> {
  const statusMap: Record<ElectiveIncidenceStatus, string> = {
    'in-preparation': isBookkeeperView
      ? i18n.t('bookkeepers.incidence.status.in_preparation_bookkeeper')
      : i18n.t('bookkeepers.incidence.status.in_preparation_admin'),
    'to-do': isBookkeeperView
      ? i18n.t('bookkeepers.incidence.status.to_do_bookkeeper')
      : i18n.t('bookkeepers.incidence.status.to_do_admin'),
    doing: i18n.t('bookkeepers.incidence.status.doing'),
    done: i18n.t('bookkeepers.incidence.status.done'),
    discarded: i18n.t('bookkeepers.incidence.status.discarded'),
  }
  return statusMap
}

/**
 * Memoizing these because this is used fairly heavily. Need to keep
 * a version of the copies per view.
 */
let bookkeeperStatuses: Record<IncidenceStatus, string>
let adminStatuses: Record<IncidenceStatus, string>
export function allStatusCopies(isBookkeeperView = false): Record<IncidenceStatus, string> {
  let statusMap: Record<IncidenceStatus, string>

  if (isBookkeeperView) {
    if (!bookkeeperStatuses) {
      bookkeeperStatuses = {
        ...professionalsStatusCopies(isBookkeeperView),
        open: i18n.t('bookkeepers.incidence.status.open'),
        reopened: i18n.t('bookkeepers.incidence.status.reopened'),
      }
    }
    statusMap = bookkeeperStatuses
  } else {
    if (!adminStatuses) {
      adminStatuses = {
        ...professionalsStatusCopies(isBookkeeperView),
        open: i18n.t('bookkeepers.incidence.status.open'),
        reopened: i18n.t('bookkeepers.incidence.status.reopened'),
      }
    }
    statusMap = adminStatuses
  }
  return statusMap
}

export type TDataViewKeys = keyof typeof DATA_VIEW
export type TDataViewValues = 'bookkeeper' | 'incidence-manager' | 'payroll-in-house'

const terminalStatuses: IncidenceStatus[] = [INCIDENCE_STATUSES.discarded, INCIDENCE_STATUSES.done]
export function isTerminalStatus(status: string) {
  return isValidIncidenceStatus(status) && terminalStatuses.includes(status)
}

export function isValidIncidenceStatus(key: string): key is IncidenceStatus {
  return Object.keys(allStatusCopies()).includes(key)
}
